import type { DropdownConfiguration, DropdownData, FilteredValue, Value } from '../interfaces/input.interface';

type DropdownOption<T> = Value<T> | FilteredValue<T>;

type DropdownNormalizationConfig = Pick<DropdownConfiguration<any>, 'controlValueKey' | 'distinctBy' | 'rawIdentityKeys' | 'valuePrimaryKey'> | undefined;

const PLACEHOLDER_KEY = '__placeholder__';

/** Normalizes dropdown option lists after any data load, filter or append operation. */
export class ReactiveDropdownOptionNormalizer {
    public static normalizeData<T>(
        dropdownData: DropdownData<T>,
        config: DropdownNormalizationConfig,
    ): DropdownData<T> {
        return {
            ...dropdownData,
            values: ReactiveDropdownOptionNormalizer.normalizeOptions(dropdownData.values ?? [], config) as Value<T>[],
            filtered: ReactiveDropdownOptionNormalizer.normalizeOptions(dropdownData.filtered ?? [], config) as FilteredValue<T>[],
        };
    }

    public static normalizeOptions<T>(
        options: ReadonlyArray<DropdownOption<T>>,
        config: DropdownNormalizationConfig,
    ): DropdownOption<T>[] {
        const seen = new Set<string>();
        const normalized: DropdownOption<T>[] = [];

        for (const option of options) {
            const key = ReactiveDropdownOptionNormalizer.getOptionKey(option, config);

            if (key !== null && seen.has(key)) {
                continue;
            }

            if (key !== null) {
                seen.add(key);
            }

            normalized.push(option);
        }

        return normalized;
    }

    private static getOptionKey<T>(option: DropdownOption<T>, config: DropdownNormalizationConfig): string | null {
        if (ReactiveDropdownOptionNormalizer.isPlaceholder(option)) {
            return PLACEHOLDER_KEY;
        }

        const distinctBy = config?.distinctBy;

        if (typeof distinctBy === 'function') {
            return ReactiveDropdownOptionNormalizer.toNormalizedKey(distinctBy(option as FilteredValue<unknown>));
        }

        if (Array.isArray(distinctBy) && distinctBy.length > 0) {
            return ReactiveDropdownOptionNormalizer.toNormalizedKey(
                distinctBy.map((path) => ReactiveDropdownOptionNormalizer.readPath(option, path)).join('|'),
            );
        }

        const identityKeys = ReactiveDropdownOptionNormalizer.getIdentityKeys(config);

        for (const identityKey of identityKeys) {
            const primaryValue = ReactiveDropdownOptionNormalizer.readPath(option, `value.${identityKey}`)
                ?? ReactiveDropdownOptionNormalizer.readPath(option, `raw.${identityKey}`)
                ?? ReactiveDropdownOptionNormalizer.readPath(option, identityKey);
            const primaryKeyValue = ReactiveDropdownOptionNormalizer.toNormalizedKey(primaryValue);

            if (primaryKeyValue !== null) {
                return primaryKeyValue;
            }
        }

        const valueKey = ReactiveDropdownOptionNormalizer.toNormalizedKey(ReactiveDropdownOptionNormalizer.readPath(option, 'value'));

        if (valueKey !== null) {
            return valueKey;
        }

        return ReactiveDropdownOptionNormalizer.toNormalizedKey(option.label);
    }


    private static getIdentityKeys(config: DropdownNormalizationConfig): string[] {
        const keys = new Set<string>();
        const candidates = [
            config?.controlValueKey,
            config?.valuePrimaryKey,
            ...(config?.rawIdentityKeys ?? []),
            'id',
        ];

        for (const candidate of candidates) {
            const key = String(candidate ?? '').trim();

            if (key !== '') {
                keys.add(key);
            }
        }

        return Array.from(keys);
    }

    private static isPlaceholder<T>(option: DropdownOption<T>): boolean {
        return option.value === null && String(option.label ?? '').trim() !== '';
    }

    private static readPath(value: unknown, path: string): unknown {
        return path
            .split('.')
            .reduce<unknown>((current, key) => {
                if (current !== null && typeof current === 'object') {
                    return (current as Record<string, unknown>)[key];
                }

                return undefined;
            }, value);
    }

    private static toNormalizedKey(value: unknown): string | null {
        if (value === null || value === undefined) {
            return null;
        }

        const normalized = String(value)
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();

        return normalized === '' ? null : normalized;
    }
}
